import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, useAuth } from "@/lib/auth";
import { useLang } from "@/lib/lang";
import { toast } from "sonner";
import { Mic, MicOff, Camera, CameraOff, Square, Eye, Activity, Hand, FileText, Image as ImageIcon, Type, X, Loader2, Clock, Lock } from "lucide-react";
import { FaceLandmarker, PoseLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import CameraConsentModal from "@/components/CameraConsentModal";

const MP_WASM_URL = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.17/wasm";
const FACE_MODEL_URL = "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";
const POSE_MODEL_URL = "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task";

const SCENARIOS = ["debate", "interview", "sales", "speech", "hostile"];

export default function Practice() {
    const { t, lang } = useLang();
    const { user, tokens, allowedScenarios, refresh } = useAuth();
    const nav = useNavigate();
    const aiName = (user?.ai_name || "IA").trim() || "IA";
    const isScenarioLocked = (s) => Array.isArray(allowedScenarios) && !allowedScenarios.includes(s);

    const [phase, setPhase] = useState("setup"); // setup | live
    const [scenario, setScenario] = useState("debate");
    const [sessLang, setSessLang] = useState(lang);
    const [session, setSession] = useState(null);
    const [transcript, setTranscript] = useState([]); // {speaker, text}
    const [interim, setInterim] = useState("");
    const [sttMode, setSttMode] = useState("webspeech"); // "webspeech" | "whisper"
    const [recordingWhisper, setRecordingWhisper] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const [micOn, setMicOn] = useState(false);
    const [camOn, setCamOn] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(20 * 60);
    const [aiResponding, setAiResponding] = useState(false);
    const [warn2min, setWarn2min] = useState(false);
    const [showCameraConsent, setShowCameraConsent] = useState(false);

    // Default selection is "debate", which free-plan accounts don't have —
    // once we know their real allowance, drop them onto the first scenario
    // they're actually allowed to pick instead of leaving a locked one selected.
    useEffect(() => {
        if (Array.isArray(allowedScenarios) && !allowedScenarios.includes(scenario) && allowedScenarios.length > 0) {
            setScenario(allowedScenarios[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowedScenarios]);

    // --- Context (script / pdf / image) ---
    const [contextMode, setContextMode] = useState("none"); // none | text | pdf | image
    const [contextText, setContextText] = useState("");
    const [contextTitle, setContextTitle] = useState("");
    const [contextLoading, setContextLoading] = useState(false);
    const fileInputRef = useRef(null);
    const imgInputRef = useRef(null);

    const [mpMetrics, setMpMetrics] = useState({
        eye_contact_pct: 0,
        blink_rate: 0,
        rigidity_score: 0,
        face_detected: false,
    });
    const [coachTip, setCoachTip] = useState("");
    const coachFrameRef = useRef(0);

    const handleAutoEndRef = useRef(null);
    const recognitionRef = useRef(null);
    const whisperRecorderRef = useRef(null);
    const whisperChunksRef = useRef([]);
    const whisperStreamRef = useRef(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const mpDetectorRef = useRef(null);
    const faceLandmarkerRef = useRef(null);
    const poseLandmarkerRef = useRef(null);
    const prevPoseRef = useRef(null);
    const [mpModelsReady, setMpModelsReady] = useState(false);
    const [mpLoadError, setMpLoadError] = useState(false);
    const utteranceBufferRef = useRef("");
    const blinkCountRef = useRef(0);
    const sessionStartRef = useRef(null);
    const lastEyeStateRef = useRef("open");
    const eyeContactSamplesRef = useRef({ in: 0, total: 0 });
    // Refs to escape stale closures (Web Speech callbacks are long-lived)
    const micOnRef = useRef(false);
    const aiSpeakingRef = useRef(false);
    const sessionRef = useRef(null);
    const sessLangRef = useRef(lang);
    const recognizingRef = useRef(false);
    const lastResultAtRef = useRef(Date.now());
    const voicesRef = useRef([]);
    const watchdogRef = useRef(null);

    // Keep refs in sync with state to avoid stale closures
    useEffect(() => { micOnRef.current = micOn; }, [micOn]);
    useEffect(() => { sessionRef.current = session; }, [session]);
    useEffect(() => { sessLangRef.current = sessLang; }, [sessLang]);

    // Release MediaPipe WASM/GPU resources when leaving the practice page
    useEffect(() => {
        return () => {
            try { faceLandmarkerRef.current?.close(); } catch (_) {}
            try { poseLandmarkerRef.current?.close(); } catch (_) {}
        };
    }, []);

    // --- Pre-load TTS voices and choose best natural one ---
    useEffect(() => {
        const loadVoices = () => {
            voicesRef.current = window.speechSynthesis?.getVoices?.() || [];
        };
        loadVoices();
        if (window.speechSynthesis) {
            window.speechSynthesis.onvoiceschanged = loadVoices;
        }
    }, []);

    const pickVoice = (l) => {
        const langTag = l === "es" ? "es" : l === "ca" ? "ca" : "en";
        const all = voicesRef.current || [];
        const matches = all.filter((v) => v.lang?.toLowerCase().startsWith(langTag));
        if (matches.length === 0) return null;
        // Score voices: prefer Google > Microsoft Neural > Natural > Premium > female-sounding default
        const score = (v) => {
            const n = (v.name || "").toLowerCase();
            let s = 0;
            if (n.includes("google")) s += 60;
            if (n.includes("neural")) s += 50;
            if (n.includes("natural")) s += 45;
            if (n.includes("online")) s += 30;
            if (n.includes("premium")) s += 25;
            if (n.includes("enhanced")) s += 20;
            // Specific high-quality voices we know
            const known = ["paulina", "monica", "helena", "sabina", "lucia", "elvira", "jorge",
                            "diego", "samantha", "ava", "siri", "daniel", "karen", "moira", "tessa",
                            "alex", "alva", "anna"];
            if (known.some((k) => n.includes(k))) s += 15;
            // Prefer exact lang match
            if (l === "es" && v.lang?.toLowerCase().startsWith("es-")) s += 5;
            if (l === "ca" && v.lang?.toLowerCase().startsWith("ca")) s += 5;
            if (l === "en" && v.lang?.toLowerCase().startsWith("en-")) s += 5;
            return s;
        };
        return matches.sort((a, b) => score(b) - score(a))[0];
    };

    // --- Start session ---
    const startSession = async () => {
        if (isScenarioLocked(scenario)) {
            toast.error(t("practice.scenario_locked"));
            nav("/pricing");
            return;
        }
        if (tokens <= 0) {
            toast.error("Sin fichas. Sube de plan.");
            nav("/pricing");
            return;
        }
        try {
            const r = await api.post("/sessions/start", {
                scenario,
                language: sessLang,
                context_text: contextText || null,
                context_title: contextTitle || null,
            });
            setSession(r.data);
            setPhase("live");
            sessionStartRef.current = Date.now();
            setSecondsLeft(r.data.duration_seconds);
            await refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || t("common.error"));
        }
    };

    // --- Upload PDF or image and extract text ---
    const handleFileUpload = async (file, kind) => {
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Archivo demasiado grande (máx 8 MB).");
            return;
        }
        setContextLoading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/context/extract", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setContextText(r.data.text);
            setContextTitle(file.name);
            toast.success(`${r.data.chars} caracteres extraídos`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo extraer el texto.");
            setContextMode("none");
        } finally {
            setContextLoading(false);
        }
    };

    const clearContext = () => {
        setContextText("");
        setContextTitle("");
        setContextMode("none");
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (imgInputRef.current) imgInputRef.current.value = "";
    };

    // --- Timer ---
    useEffect(() => {
        if (phase !== "live") return;
        const iv = setInterval(() => {
            setSecondsLeft((s) => {
                if (s <= 1) {
                    clearInterval(iv);
                    handleAutoEndRef.current?.();
                    return 0;
                }
                if (s === 120) setWarn2min(true);
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(iv);
    }, [phase]);

    // Camera is on by default so the user can see themselves while practising —
    // but only after explicit consent (required before any face/pose tracking
    // starts, since gaze/blink/posture metrics are biometric-adjacent data).
    useEffect(() => {
        if (phase === "live" && !camOn && !streamRef.current) {
            if (user?.camera_consent) {
                toggleCam();
            } else {
                setShowCameraConsent(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, user?.camera_consent]);

    const handleConsentDecision = (accepted) => {
        setShowCameraConsent(false);
        if (accepted) toggleCam();
    };

    // The <video> element only exists in the DOM once camOn is true (it's
    // behind a conditional render), so toggleCam's own videoRef.current
    // assignment can fire before the element has mounted and silently no-op.
    // Re-attach the already-open stream here once the ref is actually there.
    useEffect(() => {
        if (camOn && videoRef.current && streamRef.current && videoRef.current.srcObject !== streamRef.current) {
            videoRef.current.srcObject = streamRef.current;
            videoRef.current.play().catch(() => {});
        }
    }, [camOn]);

    // --- Robust speech recognition helpers ---
    const safeStartRecognition = useCallback(() => {
        const rec = recognitionRef.current;
        if (!rec) return;
        if (recognizingRef.current) return;
        if (aiSpeakingRef.current) return;
        if (!micOnRef.current) return;
        try {
            rec.start();
            recognizingRef.current = true;
        } catch (e) {
            // "InvalidStateError: recognition has already started" -> ignore
            console.debug("rec.start ignored:", e?.message);
        }
    }, []);

    const safeStopRecognition = useCallback((markOff = false) => {
        const rec = recognitionRef.current;
        if (!rec) return;
        try { rec.stop(); } catch (_) {}
        recognizingRef.current = false;
        if (markOff) micOnRef.current = false;
    }, []);

    // --- Web Speech API setup (falls back to server-side Whisper transcription
    // on browsers without support, e.g. Safari/Firefox/most mobile browsers) ---
    useEffect(() => {
        if (phase !== "live") return;
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) {
            setSttMode("whisper");
            toast.message(t("practice.whisper_fallback_notice"));
            return;
        }
        setSttMode("webspeech");
        const rec = new SR();
        rec.continuous = true;
        rec.interimResults = true;
        rec.maxAlternatives = 1;
        rec.lang = sessLang === "es" ? "es-ES" : sessLang === "ca" ? "ca-ES" : "en-US";

        rec.onstart = () => {
            recognizingRef.current = true;
            lastResultAtRef.current = Date.now();
        };
        rec.onresult = (event) => {
            lastResultAtRef.current = Date.now();
            let finalT = "";
            let interimT = "";
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const trans = event.results[i][0].transcript;
                if (event.results[i].isFinal) finalT += trans + " ";
                else interimT += trans;
            }
            if (interimT) setInterim(interimT);
            if (finalT.trim()) {
                setInterim("");
                handleUserUtterance(finalT.trim());
            }
        };
        rec.onerror = (e) => {
            console.warn("speech recognition error:", e.error);
            recognizingRef.current = false;
            // Recoverable errors: try again shortly
            const recoverable = ["no-speech", "aborted", "network", "audio-capture"];
            if (recoverable.includes(e.error) && micOnRef.current && !aiSpeakingRef.current) {
                setTimeout(safeStartRecognition, 400);
            }
            if (e.error === "not-allowed" || e.error === "service-not-allowed") {
                toast.error("Permiso de micrófono denegado.");
                micOnRef.current = false;
                setMicOn(false);
            }
        };
        rec.onend = () => {
            recognizingRef.current = false;
            // Auto-restart if mic still on and AI not speaking
            if (micOnRef.current && !aiSpeakingRef.current) {
                setTimeout(safeStartRecognition, 250);
            }
        };
        recognitionRef.current = rec;

        // Watchdog: if no result for 8s while mic on and not speaking, restart
        const wd = setInterval(() => {
            if (!micOnRef.current || aiSpeakingRef.current) return;
            const since = Date.now() - lastResultAtRef.current;
            if (since > 8000) {
                console.debug("[watchdog] restarting recognition (no activity)");
                lastResultAtRef.current = Date.now();
                if (recognizingRef.current) {
                    try { rec.stop(); } catch (_) {}
                    recognizingRef.current = false;
                } else {
                    safeStartRecognition();
                }
            }
        }, 2000);
        watchdogRef.current = wd;

        return () => {
            clearInterval(wd);
            watchdogRef.current = null;
            try { rec.abort(); } catch (_) {}
            recognizingRef.current = false;
            recognitionRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, sessLang]);

    // --- Whisper fallback: push-to-talk recording for browsers without
    // native Web Speech API support ---
    const startWhisperRecording = async () => {
        if (aiSpeakingRef.current || recordingWhisper) return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            whisperStreamRef.current = stream;
            const mimeType = MediaRecorder.isTypeSupported("audio/webm")
                ? "audio/webm"
                : (MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "");
            const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            whisperChunksRef.current = [];
            mr.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) whisperChunksRef.current.push(e.data);
            };
            mr.onstop = async () => {
                stream.getTracks().forEach((tr) => tr.stop());
                whisperStreamRef.current = null;
                const blob = new Blob(whisperChunksRef.current, { type: mr.mimeType || "audio/webm" });
                if (blob.size < 800) return; // too short to be real speech
                setTranscribing(true);
                try {
                    const form = new FormData();
                    const ext = (mr.mimeType || "").includes("mp4") ? "mp4" : "webm";
                    form.append("file", blob, `speech.${ext}`);
                    const r = await api.post("/speech/transcribe", form, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    if (r.data.text) handleUserUtterance(r.data.text);
                } catch (e) {
                    toast.error(t("practice.whisper_error"));
                } finally {
                    setTranscribing(false);
                }
            };
            mr.start();
            whisperRecorderRef.current = mr;
            setRecordingWhisper(true);
        } catch (e) {
            toast.error(t("practice.mic_denied"));
        }
    };

    const stopWhisperRecording = () => {
        const mr = whisperRecorderRef.current;
        if (mr && mr.state !== "inactive") mr.stop();
        setRecordingWhisper(false);
    };

    const handleUserUtterance = async (text) => {
        const sess = sessionRef.current;
        if (!text || !sess) return;
        utteranceBufferRef.current += " " + text;
        setTranscript((prev) => [...prev, { speaker: "user", text }]);
        setAiResponding(true);
        try {
            const r = await api.post("/sessions/chat", {
                session_id: sess.session_id,
                user_text: text,
            });
            setTranscript((prev) => [...prev, { speaker: "ai", text: r.data.ai_text }]);
            speak(r.data.ai_text, sessLangRef.current);
        } catch (e) {
            console.error(e);
            if (e.response?.status === 410) {
                handleAutoEnd();
            } else {
                toast.error(e.response?.data?.detail || t("common.error"));
            }
        } finally {
            setAiResponding(false);
        }
    };

    const speak = (text, l) => {
        if (!window.speechSynthesis) return;
        // Cancel any previous queued speech
        try { window.speechSynthesis.cancel(); } catch (_) {}

        // Pause recognition while AI speaks
        aiSpeakingRef.current = true;
        safeStopRecognition(false);

        // Break long text into sentences for smoother playback
        const sentences = text.match(/[^.!?]+[.!?]?/g) || [text];
        const voice = pickVoice(l);
        let idx = 0;
        const speakNext = () => {
            if (idx >= sentences.length) {
                aiSpeakingRef.current = false;
                if (micOnRef.current) {
                    setTimeout(safeStartRecognition, 350);
                }
                return;
            }
            const u = new SpeechSynthesisUtterance(sentences[idx].trim());
            u.lang = l === "es" ? "es-ES" : l === "ca" ? "ca-ES" : "en-US";
            if (voice) u.voice = voice;
            u.rate = 0.98;
            u.pitch = 1.02;
            u.volume = 1.0;
            u.onend = () => { idx += 1; speakNext(); };
            u.onerror = () => { idx += 1; speakNext(); };
            window.speechSynthesis.speak(u);
        };
        speakNext();
    };

    const toggleMic = () => {
        if (!recognitionRef.current) {
            toast.error("Reconocimiento de voz no disponible.");
            return;
        }
        if (micOn) {
            micOnRef.current = false;
            setMicOn(false);
            safeStopRecognition(true);
        } else {
            micOnRef.current = true;
            setMicOn(true);
            lastResultAtRef.current = Date.now();
            setTimeout(safeStartRecognition, 100);
        }
    };

    const toggleCam = async () => {
        if (camOn) {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
            }
            if (mpDetectorRef.current) {
                clearInterval(mpDetectorRef.current);
                mpDetectorRef.current = null;
            }
            setCamOn(false);
            setCoachTip("");
            coachFrameRef.current = 0;
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 } });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play().catch(() => {});
            }
            setCamOn(true);
            startMediaPipeLite();
        } catch (e) {
            toast.error("No se pudo acceder a la cámara.");
        }
    };

    // --- Real tracking via Google's MediaPipe Tasks Vision (FaceLandmarker +
    // PoseLandmarker), replacing the old canvas-brightness heuristic.
    // Face blendshapes give genuine gaze direction (eye contact) and blink
    // state; pose landmarks give real wrist/elbow visibility (arms in frame)
    // and frame-to-frame movement (rigidity). Models load lazily and are
    // cached in refs so we only download them once per session. If loading
    // fails (e.g. no network to Google's model CDN), we degrade gracefully
    // instead of crashing the practice session.
    const loadMediaPipeModels = useCallback(async () => {
        if (faceLandmarkerRef.current && poseLandmarkerRef.current) return true;
        try {
            const filesetResolver = await FilesetResolver.forVisionTasks(MP_WASM_URL);
            const [face, pose] = await Promise.all([
                FaceLandmarker.createFromOptions(filesetResolver, {
                    baseOptions: { modelAssetPath: FACE_MODEL_URL, delegate: "GPU" },
                    outputFaceBlendshapes: true,
                    runningMode: "VIDEO",
                    numFaces: 1,
                }),
                PoseLandmarker.createFromOptions(filesetResolver, {
                    baseOptions: { modelAssetPath: POSE_MODEL_URL, delegate: "GPU" },
                    runningMode: "VIDEO",
                    numPoses: 1,
                }),
            ]);
            faceLandmarkerRef.current = face;
            poseLandmarkerRef.current = pose;
            setMpModelsReady(true);
            return true;
        } catch (e) {
            console.error("MediaPipe model load failed:", e);
            setMpLoadError(true);
            return false;
        }
    }, []);

    const startMediaPipeLite = useCallback(async () => {
        if (mpDetectorRef.current) return;
        const ok = await loadMediaPipeModels();
        if (!ok) return;

        const iv = setInterval(() => {
            const v = videoRef.current;
            const face = faceLandmarkerRef.current;
            const pose = poseLandmarkerRef.current;
            if (!v || !face || !pose || v.readyState < 2) return;

            const now = performance.now();
            let faceDetected = false;
            let eyeContactFrame = 0;
            let blinkFrame = false;

            try {
                const faceResult = face.detectForVideo(v, now);
                const shapes = faceResult?.faceBlendshapes?.[0]?.categories;
                if (shapes && shapes.length) {
                    faceDetected = true;
                    const get = (name) => shapes.find((c) => c.categoryName === name)?.score || 0;
                    // Gaze deviation: how far the eyes look away from center (0 = dead-center = camera)
                    const deviation = (
                        get("eyeLookOutLeft") + get("eyeLookOutRight") +
                        get("eyeLookInLeft") + get("eyeLookInRight") +
                        get("eyeLookUpLeft") + get("eyeLookUpRight") +
                        get("eyeLookDownLeft") + get("eyeLookDownRight")
                    ) / 8;
                    eyeContactFrame = Math.max(0, 100 - deviation * 220);

                    const blinkL = get("eyeBlinkLeft");
                    const blinkR = get("eyeBlinkRight");
                    blinkFrame = blinkL > 0.6 && blinkR > 0.6;
                }
            } catch (e) {
                // transient decode error on a single frame — skip silently
            }

            if (blinkFrame && lastEyeStateRef.current === "open") {
                blinkCountRef.current += 1;
                lastEyeStateRef.current = "closed";
            } else if (!blinkFrame && lastEyeStateRef.current === "closed") {
                lastEyeStateRef.current = "open";
            }

            let armsVisible = false;
            let movement = 0;
            try {
                const poseResult = pose.detectForVideo(v, now);
                const lm = poseResult?.landmarks?.[0];
                if (lm && lm.length >= 17) {
                    // 11/12 shoulders, 13/14 elbows, 15/16 wrists
                    const trackedIdx = [11, 12, 13, 14, 15, 16];
                    armsVisible = (lm[15]?.visibility ?? 0) > 0.5 && (lm[16]?.visibility ?? 0) > 0.5;

                    if (prevPoseRef.current) {
                        let totalDelta = 0;
                        trackedIdx.forEach((i, idx) => {
                            const prev = prevPoseRef.current[idx];
                            const cur = lm[i];
                            if (prev && cur) {
                                totalDelta += Math.hypot(cur.x - prev.x, cur.y - prev.y);
                            }
                        });
                        movement = totalDelta / trackedIdx.length;
                    }
                    prevPoseRef.current = trackedIdx.map((i) => ({ x: lm[i].x, y: lm[i].y }));
                }
            } catch (e) {
                // transient decode error on a single frame — skip silently
            }

            eyeContactSamplesRef.current.total += 1;
            if (faceDetected) eyeContactSamplesRef.current.in += eyeContactFrame / 100;

            const eye_contact_pct = (eyeContactSamplesRef.current.in / Math.max(eyeContactSamplesRef.current.total, 1)) * 100;
            const elapsedMin = (Date.now() - (sessionStartRef.current || Date.now())) / 60000;
            const blink_rate = elapsedMin > 0.1 ? blinkCountRef.current / elapsedMin : 0;
            // movement is a normalized-coordinate delta (0-1 scale); scale up for a 0-100 rigidity score
            const rigidity_score = Math.max(0, 100 - movement * 3000);

            setMpMetrics({
                eye_contact_pct: Math.min(100, eye_contact_pct),
                blink_rate,
                rigidity_score: Math.min(100, rigidity_score),
                face_detected: faceDetected,
            });

            // Posture coach: re-evaluate roughly every ~12s (48 ticks @ 250ms)
            // so tips don't flicker, and prioritize the single most useful tip.
            coachFrameRef.current += 1;
            if (coachFrameRef.current % 48 === 0) {
                let tip = "";
                if (rigidity_score > 75) tip = t("practice.coach.too_stiff");
                else if (rigidity_score < 25) tip = t("practice.coach.too_much_movement");
                else if (eye_contact_pct < 40) tip = t("practice.coach.low_eye_contact");
                else if (!armsVisible) tip = t("practice.coach.arms_not_visible");
                else if (blink_rate > 25) tip = t("practice.coach.nervous_blink");
                else tip = t("practice.coach.good");
                setCoachTip(tip);
            }
        }, 250);
        mpDetectorRef.current = iv;
    }, [loadMediaPipeModels, t]);

    const handleAutoEnd = async () => {
        toast.warning(t("practice.session_expired"));
        await endNow(true);
    };
    handleAutoEndRef.current = handleAutoEnd;

    const [extending, setExtending] = useState(false);

    const extendSession = async () => {
        if (!session || extending) return;
        if (tokens <= 0) {
            toast.error(t("practice.extend_no_tokens"));
            return;
        }
        setExtending(true);
        try {
            await api.post(`/sessions/${session.session_id}/extend`);
            setSecondsLeft((s) => s + 5 * 60);
            setWarn2min(false);
            await refresh();
            toast.success(t("practice.extend_success"));
        } catch (e) {
            toast.error(e.response?.data?.detail || t("common.error"));
        } finally {
            setExtending(false);
        }
    };

    const endSession = () => endNow(false);

    const endNow = async (forced) => {
        if (!session) return;
        try {
            // Mark mic off so callbacks don't try to restart
            micOnRef.current = false;
            aiSpeakingRef.current = false;
            if (watchdogRef.current) { clearInterval(watchdogRef.current); watchdogRef.current = null; }
            if (recognitionRef.current) { try { recognitionRef.current.abort(); } catch (_) {} }
            if (whisperRecorderRef.current && whisperRecorderRef.current.state !== "inactive") {
                try { whisperRecorderRef.current.stop(); } catch (_) {}
            }
            if (whisperStreamRef.current) { whisperStreamRef.current.getTracks().forEach((tr) => tr.stop()); }
            if (window.speechSynthesis) window.speechSynthesis.cancel();
            if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); }
            if (mpDetectorRef.current) clearInterval(mpDetectorRef.current);

            const r = await api.post("/sessions/end", {
                session_id: session.session_id,
                transcript_full: transcript.filter(t => t.speaker === "user").map(t => t.text).join(" ") + " " + utteranceBufferRef.current,
                mediapipe_metrics: mpMetrics,
            });
            (r.data.unlocked_achievements || []).forEach((a) => {
                toast.success(`${t("achievements.toast_title")} ${a.title} (+${a.reward} ${a.reward === 1 ? t("achievements.reward") : t("achievements.rewards")})`);
            });
            nav(`/session/${r.data.id}`);
        } catch (e) {
            console.error(e);
            toast.error(t("common.error"));
        }
    };

    useEffect(() => {
        return () => {
            micOnRef.current = false;
            aiSpeakingRef.current = false;
            if (watchdogRef.current) clearInterval(watchdogRef.current);
            if (recognitionRef.current) { try { recognitionRef.current.abort(); } catch (_) {} }
            if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
            if (mpDetectorRef.current) clearInterval(mpDetectorRef.current);
            if (window.speechSynthesis) window.speechSynthesis.cancel();
        };
    }, []);

    const minutes = Math.floor(secondsLeft / 60);
    const secs = secondsLeft % 60;
    const timeStr = `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    const timerCritical = secondsLeft <= 120;

    if (phase === "setup") {
        return (
            <div className="min-h-screen bg-ink text-slate-900">
                <Navbar />
                <main className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
                    <div className="mono-label mb-3">/ NEW SESSION</div>
                    <h1 className="font-display text-5xl md:text-6xl tracking-tighter mb-10" data-testid="practice-setup-title">
                        {t("practice.choose_scenario")}
                    </h1>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-100 border border-slate-200 mb-10" data-testid="scenarios-grid">
                        {SCENARIOS.map((s) => {
                            const locked = isScenarioLocked(s);
                            return (
                                <button
                                    key={s}
                                    onClick={() => locked ? nav("/pricing") : setScenario(s)}
                                    className={`bg-ink p-8 text-left transition-colors ${locked ? "opacity-50 hover:bg-slate-50" : scenario === s ? "ring-1 ring-brand" : "hover:bg-slate-50"}`}
                                    data-testid={`scenario-${s}`}
                                    title={locked ? t("practice.scenario_locked") : undefined}
                                >
                                    <div className="mono-label text-brand mb-3 flex items-center gap-1.5">
                                        {locked ? (
                                            <>
                                                <Lock size={11} /> {t("practice.upgrade_required")}
                                            </>
                                        ) : scenario === s ? "● SELECTED" : "○ SELECT"}
                                    </div>
                                    <h3 className="font-display text-2xl mb-2">
                                        {t(`practice.scenarios.${s}`)}
                                    </h3>
                                    <p className="text-slate-600 text-sm">
                                        {t(`practice.scenarios_desc.${s}`)}
                                    </p>
                                </button>
                            );
                        })}
                    </div>
                    <div className="mb-10">
                        <div className="mono-label mb-3">{t("practice.language")}</div>
                        <div className="flex gap-3" data-testid="session-lang-options">
                            {["es", "en", "ca"].map((l) => (
                                <button
                                    key={l}
                                    onClick={() => setSessLang(l)}
                                    className={`px-5 py-2 border ${sessLang === l ? "border-brand text-brand" : "border-slate-300 text-slate-600"} transition-colors mono-label rounded-md`}
                                    data-testid={`session-lang-${l}`}
                                >
                                    {l.toUpperCase()}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Context block */}
                    <div className="mb-10" data-testid="context-block">
                        <div className="mono-label mb-3 flex items-center justify-between">
                            <span>GUION / MATERIAL DE REFERENCIA <span className="text-slate-400">(opcional)</span></span>
                            {contextText && (
                                <button onClick={clearContext} className="text-brand hover:underline normal-case tracking-normal text-xs flex items-center gap-1" data-testid="clear-context-btn">
                                    <X size={12} /> quitar
                                </button>
                            )}
                        </div>
                        {!contextText && (
                            <div className="grid grid-cols-3 gap-3 mb-4" data-testid="context-mode-options">
                                <ContextModeBtn icon={<Type size={16} />} label="Texto" active={contextMode === "text"} onClick={() => setContextMode("text")} testid="ctx-mode-text" />
                                <ContextModeBtn icon={<FileText size={16} />} label="PDF" active={contextMode === "pdf"} onClick={() => { setContextMode("pdf"); fileInputRef.current?.click(); }} testid="ctx-mode-pdf" />
                                <ContextModeBtn icon={<ImageIcon size={16} />} label="Foto del guion" active={contextMode === "image"} onClick={() => { setContextMode("image"); imgInputRef.current?.click(); }} testid="ctx-mode-image" />
                            </div>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="application/pdf,.pdf"
                            className="hidden"
                            onChange={(e) => handleFileUpload(e.target.files?.[0], "pdf")}
                            data-testid="ctx-pdf-input"
                        />
                        <input
                            ref={imgInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                            className="hidden"
                            onChange={(e) => handleFileUpload(e.target.files?.[0], "image")}
                            data-testid="ctx-image-input"
                        />
                        {contextLoading && (
                            <div className="card-surface p-6 flex items-center gap-3 text-slate-600" data-testid="ctx-loading">
                                <Loader2 size={18} className="animate-spin text-brand" />
                                Extrayendo texto del archivo...
                            </div>
                        )}
                        {contextMode === "text" && !contextText && !contextLoading && (
                            <div className="card-surface p-4">
                                <textarea
                                    value={contextText}
                                    onChange={(e) => setContextText(e.target.value)}
                                    onBlur={() => { if (contextText.trim() && !contextTitle) setContextTitle("Texto pegado"); }}
                                    rows={6}
                                    placeholder="Pega aquí tu guion, notas o el tema sobre el que quieres practicar..."
                                    className="w-full bg-white border border-slate-200 px-4 py-3 rounded-md focus:border-brand outline-none transition-colors text-slate-900 resize-y"
                                    data-testid="ctx-textarea"
                                />
                            </div>
                        )}
                        {contextText && (
                            <div className="card-surface p-5" data-testid="ctx-preview">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="mono-label flex items-center gap-2">
                                        <FileText size={14} className="text-brand" />
                                        {contextTitle || "Material cargado"}
                                    </div>
                                    <span className="font-mono text-xs text-slate-500">{contextText.length} chars</span>
                                </div>
                                <p className="text-sm text-slate-700 line-clamp-4 whitespace-pre-wrap leading-relaxed">
                                    {contextText.slice(0, 600)}{contextText.length > 600 ? "..." : ""}
                                </p>
                            </div>
                        )}
                    </div>

                    <button onClick={startSession} disabled={contextLoading} className="btn-primary" data-testid="confirm-start-session-btn">
                        {t("practice.start")}
                    </button>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white text-slate-900">
            {showCameraConsent && <CameraConsentModal onDecision={handleConsentDecision} />}
            {/* No regular navbar in cinematic mode */}
            <div className="absolute top-0 left-0 right-0 z-40 px-6 py-5 flex items-center justify-between glass">
                <div className="flex items-center gap-3">
                    <img src="/logo.png" alt="Vivoz" className="h-7 w-auto" />
                    <span className="mono-label text-slate-500 ml-4">/ {scenario.toUpperCase()}</span>
                </div>
                <div className={`font-mono text-2xl tracking-widest ${timerCritical ? "text-brand animate-pulse" : ""}`} data-testid="timer-display">
                    {timeStr}
                </div>
                <button onClick={endSession} className="btn-primary text-sm" data-testid="end-session-btn">
                    <Square size={14} fill="currentColor" /> {t("practice.end_session")}
                </button>
            </div>

            <div className="grid lg:grid-cols-12 min-h-screen pt-20">
                {/* Camera area */}
                <div className="lg:col-span-8 relative bg-white flex items-center justify-center border-r border-slate-200">
                    {camOn ? (
                        <>
                            <video ref={videoRef} autoPlay playsInline muted className="max-h-[80vh] w-auto" data-testid="practice-video" />
                            <canvas ref={canvasRef} className="hidden" />
                            {/* MediaPipe overlay indicator */}
                            <div className="absolute top-6 left-6 glass px-3 py-2 mono-label flex items-center gap-2" data-testid="mp-status">
                                <span className={`w-2 h-2 rounded-full ${mpMetrics.face_detected ? "bg-brand animate-pulse" : "bg-slate-300"}`} />
                                MEDIAPIPE {mpMetrics.face_detected ? "ACTIVE" : "SEARCHING"}
                            </div>
                        </>
                    ) : (
                        <div className="text-center max-w-md px-6">
                            <Camera size={48} className="mx-auto mb-6 text-slate-400" />
                            <p className="text-slate-500 mb-6 font-light">
                                Activa la cámara para análisis de postura con MediaPipe.
                            </p>
                            <button onClick={toggleCam} className="btn-ghost" data-testid="toggle-cam-btn">
                                <Camera size={16} /> {t("practice.cam_off")}
                            </button>
                        </div>
                    )}
                </div>

                {/* Right panel */}
                <div className="lg:col-span-4 flex flex-col bg-white">
                    {/* Telemetry */}
                    <div className="p-6 border-b border-slate-200">
                        <div className="mono-label mb-4">/ TRACKING</div>
                        {camOn && !mpModelsReady && !mpLoadError && (
                            <div className="flex items-center gap-2 text-xs text-slate-500 mb-3" data-testid="mp-loading">
                                <Loader2 size={13} className="animate-spin" /> {t("practice.mp_loading")}
                            </div>
                        )}
                        {mpLoadError && (
                            <div className="text-xs text-amber-600 mb-3" data-testid="mp-load-error">
                                {t("practice.mp_load_error")}
                            </div>
                        )}
                        <div className="space-y-3" data-testid="mediapipe-metrics">
                            <Metric icon={<Eye size={14} />} label={t("practice.tracking.eye")} value={`${Math.round(mpMetrics.eye_contact_pct)}%`} pct={mpMetrics.eye_contact_pct} />
                            <Metric icon={<Activity size={14} />} label={t("practice.tracking.blink")} value={`${Math.round(mpMetrics.blink_rate)}/min`} pct={Math.min(100, mpMetrics.blink_rate * 5)} />
                            <Metric icon={<Hand size={14} />} label={t("practice.tracking.rigidity")} value={`${Math.round(mpMetrics.rigidity_score)}`} pct={mpMetrics.rigidity_score} />
                        </div>
                        {coachTip && (
                            <div className="mt-4 border border-brand/40 bg-brand/5 px-4 py-3 flex items-start gap-2" data-testid="coach-tip">
                                <Hand size={14} className="text-brand mt-0.5 flex-shrink-0" />
                                <div>
                                    <div className="mono-label text-brand mb-1">{t("practice.coach.label")}</div>
                                    <p className="text-sm text-slate-700">{coachTip}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Transcript */}
                    <div className="flex-1 overflow-y-auto p-6 border-b border-slate-200 max-h-[40vh]" data-testid="transcript-pane">
                        <div className="mono-label mb-4">/ {t("practice.transcript")}</div>
                        {transcript.length === 0 && !interim && (
                            <p className="text-slate-400 text-sm font-light">{t("practice.speak_now")}</p>
                        )}
                        <div className="space-y-3 text-sm">
                            {transcript.map((line, i) => (
                                <div key={i} className={line.speaker === "ai" ? "text-brand" : "text-slate-800"}>
                                    <span className="mono-label mr-2">{line.speaker === "ai" ? aiName.toUpperCase() : "YOU"}</span>
                                    {line.text}
                                </div>
                            ))}
                            {interim && (
                                <div className="text-slate-500 italic">
                                    <span className="mono-label mr-2">YOU</span>
                                    {interim}
                                </div>
                            )}
                            {aiResponding && (
                                <div className="text-brand/70 mono-label animate-pulse">/ {t("practice.ai_speaking")}</div>
                            )}
                        </div>
                    </div>

                    {/* Controls */}
                    <div className="p-6 space-y-3">
                        {sttMode === "whisper" ? (
                            <button
                                onMouseDown={startWhisperRecording}
                                onMouseUp={stopWhisperRecording}
                                onMouseLeave={() => recordingWhisper && stopWhisperRecording()}
                                onTouchStart={startWhisperRecording}
                                onTouchEnd={stopWhisperRecording}
                                disabled={transcribing}
                                className={`w-full justify-center select-none ${recordingWhisper ? "btn-primary" : "btn-ghost"} disabled:opacity-60`}
                                data-testid="whisper-push-to-talk-btn"
                            >
                                {transcribing ? (
                                    <><Loader2 size={16} className="animate-spin" /> {t("practice.transcribing")}</>
                                ) : recordingWhisper ? (
                                    <><Mic size={16} /> {t("practice.recording_release")}</>
                                ) : (
                                    <><Mic size={16} /> {t("practice.hold_to_talk")}</>
                                )}
                            </button>
                        ) : (
                            <button onClick={toggleMic} className={`w-full justify-center ${micOn ? "btn-primary" : "btn-ghost"}`} data-testid="toggle-mic-btn">
                                {micOn ? <><MicOff size={16} /> {t("practice.mic_on")}</> : <><Mic size={16} /> {t("practice.mic_off")}</>}
                            </button>
                        )}
                        <button onClick={toggleCam} className="btn-ghost w-full justify-center" data-testid="toggle-cam-control">
                            {camOn ? <><CameraOff size={16} /> {t("practice.cam_on")}</> : <><Camera size={16} /> {t("practice.cam_off")}</>}
                        </button>
                        {warn2min && (
                            <div className="text-xs text-brand border border-brand/40 p-3 mt-3 space-y-2" data-testid="warn-2min">
                                <div>{t("practice.warn_2min")}</div>
                                <button
                                    onClick={extendSession}
                                    disabled={extending || tokens <= 0}
                                    className="w-full justify-center btn-primary py-2 text-xs disabled:opacity-50"
                                    data-testid="extend-session-btn"
                                >
                                    <Clock size={14} /> {t("practice.extend_cta")}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Metric({ icon, label, value, pct }) {
    return (
        <div>
            <div className="flex justify-between items-center mb-1">
                <span className="mono-label flex items-center gap-2 text-slate-600">{icon} {label}</span>
                <span className="font-mono text-sm text-slate-900">{value}</span>
            </div>
            <div className="h-1 bg-slate-100">
                <div className="h-full bg-brand transition-all duration-300" style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
        </div>
    );
}

function ContextModeBtn({ icon, label, active, onClick, testid }) {
    return (
        <button
            onClick={onClick}
            className={`card-surface p-4 text-left flex flex-col gap-2 transition-all hover:border-brand hover:bg-slate-50 ${active ? "border-brand bg-slate-50" : ""}`}
            data-testid={testid}
        >
            <span className={`${active ? "text-brand" : "text-slate-600"}`}>{icon}</span>
            <span className="text-sm font-medium text-slate-900">{label}</span>
        </button>
    );
}

